import { createFileRoute } from '@tanstack/react-router';
import { Banner } from '@osd/ui/banner';
import { Spinner } from '@osd/ui/spinner';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/spinner — live docs for the `Spinner` primitive.
 *
 * Spinner is the animated loading glyph. The production app uses
 * `<Loader />` from `@osd/icons` (added 2026-09-22 — root/osd#123).
 */
export const Route = createFileRoute('/ui/_layout/spinner')({
  component: SpinnerPage,
});

function SpinnerPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Spinner"
        hint="Animated loading glyph — 5 sizes, optional label, role=status + aria-live=polite"
      >
        <Text>
          The wrapper renders as <code>role=&quot;status&quot;</code> with{' '}
          <code>aria-live=&quot;polite&quot;</code>, so screen readers announce the spinner once on
          mount (the polite tier queues the announcement instead of interrupting the user&rsquo;s
          current focus).
        </Text>
      </Section>

      <Section title="Sizes" hint="xs / sm / md / lg / xl">
        <Stack direction="row" gap={4} align="center">
          <Spinner size="xs" />
          <Spinner size="sm" />
          <Spinner size="md" />
          <Spinner size="lg" />
          <Spinner size="xl" />
        </Stack>
      </Section>

      <Section title="With label" hint="Visible + accessible label">
        <Stack direction="row" gap={4} align="center">
          <Spinner label="Loading users…" />
          <Spinner size="lg" label="Submitting" />
        </Stack>
      </Section>

      <Section title="Inline" hint="Inside a sentence">
        <Text>
          Saving your changes
          <Spinner inline size="xs" /> please wait.
        </Text>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Basic"
            code={`<Spinner />
<Spinner size="lg" label="Loading users…" />`}
          />
          <CodeBlock
            language="tsx"
            title="Inline in a sentence"
            code={`<p>
  Submitting <Spinner inline size="xs" /> please wait.
</p>`}
          />
          <CodeBlock
            language="tsx"
            title="Custom accessible name"
            code={`<Spinner ariaLabel="Submitting form" />`}
          />
        </Stack>
      </Section>

      <Banner variant="info" title="Reduced motion">
        Under <code>prefers-reduced-motion: reduce</code> the spin animation duration drops to{' '}
        <code>0s</code>; the ring stays visible as a static accent-coloured arc. Honours WCAG 2.3.3.
      </Banner>
    </Stack>
  );
}
